import type { FavoriteProduct, FavoritesListResponse } from "./favorites.types";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/** API may return string URLs or `{ url: string }` image rows */
function coerceImageList(images: unknown): string[] {
  if (!Array.isArray(images)) return [];
  const out: string[] = [];
  for (const img of images) {
    if (typeof img === "string" && img.trim()) out.push(img);
    else if (isRecord(img) && typeof img.url === "string" && img.url.trim()) {
      out.push(img.url.trim());
    }
  }
  return out;
}

function coerceSeller(
  source: Record<string, unknown>
): FavoriteProduct["seller"] | undefined {
  const s = source.seller;
  if (isRecord(s)) {
    const name = typeof s.name === "string" ? s.name : undefined;
    if (name) {
      return {
        id: String(s.id ?? s.userId ?? ""),
        name,
        email: typeof s.email === "string" ? s.email : undefined,
      };
    }
  }
  const company = source.company;
  if (isRecord(company) && typeof company.name === "string") {
    return {
      id: String(company.id ?? ""),
      name: company.name,
    };
  }
  return undefined;
}

/**
 * Maps one API row to `FavoriteProduct`.
 * Supports:
 * - Flat product rows (already `FavoriteProduct`-shaped)
 * - Favorite join rows: `{ productId, createdAt, product: { ... } }`
 */
function normalizeFavoriteRow(item: unknown): FavoriteProduct | null {
  if (!isRecord(item)) return null;

  if (isRecord(item.product)) {
    const p = item.product;
    const favoritedAt =
      (typeof item.createdAt === "string" && item.createdAt) ||
      (typeof item.favoritedAt === "string" && item.favoritedAt) ||
      (typeof p.favoritedAt === "string" && p.favoritedAt) ||
      new Date().toISOString();

    const id = String(p.id ?? item.productId ?? "");
    if (!id) return null;

    return {
      id,
      name: String(p.name ?? ""),
      description: typeof p.description === "string" ? p.description : undefined,
      price: Number(p.price ?? 0),
      images: coerceImageList(p.images),
      thumbnailImage:
        typeof p.thumbnailImage === "string" ? p.thumbnailImage : undefined,
      sku: String(p.sku ?? ""),
      category: typeof p.category === "string" ? p.category : undefined,
      brand: typeof p.brand === "string" ? p.brand : undefined,
      moq: typeof p.moq === "number" ? p.moq : undefined,
      currency: typeof p.currency === "string" ? p.currency : undefined,
      seller: coerceSeller(p),
      favoritedAt,
    };
  }

  const id = String(item.id ?? "");
  if (!id) return null;

  const favoritedAt =
    (typeof item.favoritedAt === "string" && item.favoritedAt) ||
    (typeof item.createdAt === "string" && item.createdAt) ||
    new Date().toISOString();

  return {
    id,
    name: String(item.name ?? ""),
    description: typeof item.description === "string" ? item.description : undefined,
    price: Number(item.price ?? 0),
    images: coerceImageList(item.images),
    thumbnailImage:
      typeof item.thumbnailImage === "string" ? item.thumbnailImage : undefined,
    sku: String(item.sku ?? ""),
    category: typeof item.category === "string" ? item.category : undefined,
    brand: typeof item.brand === "string" ? item.brand : undefined,
    moq: typeof item.moq === "number" ? item.moq : undefined,
    currency: typeof item.currency === "string" ? item.currency : undefined,
    seller: coerceSeller(item),
    favoritedAt,
  };
}

function extractRawFavoritesArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (!isRecord(raw)) return [];

  const top = raw.favorites;
  if (Array.isArray(top)) return top;

  const data = raw.data;
  if (Array.isArray(data)) return data;
  if (isRecord(data)) {
    const nested = data.favorites;
    if (Array.isArray(nested)) return nested;
  }

  return [];
}

/**
 * Normalizes GET /favorites payloads across common backend shapes:
 * `{ favorites: [...] }`, `{ success, data: { favorites: [...] } }`, or a bare array.
 */
export function parseFavoritesListPayload(raw: unknown): FavoritesListResponse {
  const list = extractRawFavoritesArray(raw);
  const favorites = list
    .map(normalizeFavoriteRow)
    .filter((row): row is FavoriteProduct => row != null);

  let total = favorites.length;
  let success = true;
  let message = "";

  if (isRecord(raw)) {
    if (typeof raw.total === "number") total = raw.total;
    else if (isRecord(raw.data) && typeof raw.data.total === "number") {
      total = raw.data.total as number;
    }
    if (typeof raw.success === "boolean") success = raw.success;
    if (typeof raw.message === "string") message = raw.message;
  }

  return {
    favorites,
    total,
    success,
    message,
  };
}
